import { useEffect, useRef, useState, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { getRepresentativeToken, RepCommMessage } from '../services/representativeService';

const SOCKET_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/graphql', '').replace('/api', '')
  : 'http://localhost:5000';

interface TypingUser {
  representativeId: string;
  name: string;
}

interface UseRepresentativeCommSocketOptions {
  onNewMessage?: (message: RepCommMessage) => void;
  onConversationUpdated?: (data: { conversationId: string }) => void;
  onMessageError?: (data: { conversationId: string; error: string; content?: string }) => void;
  enabled?: boolean;
}

// Dopo quanto un "sta scrivendo…" scade da solo se non rinnovato
// (copre il mittente che chiude il tab prima di emettere stop_typing)
const TYPING_EXPIRE_MS = 4000;

/**
 * Socket della chat tra rappresentanti (namespace /representative-comm).
 * Una sola connessione per pagina: il join/leave delle conversazioni è
 * esposto come metodo, così il thread aperto può cambiare senza riconnettere.
 */
export const useRepresentativeCommSocket = ({
  onNewMessage,
  onConversationUpdated,
  onMessageError,
  enabled = true
}: UseRepresentativeCommSocketOptions) => {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);
  const [onlineReps, setOnlineReps] = useState<string[]>([]);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Timer di scadenza per ogni "sta scrivendo…" ricevuto
  const typingExpireRef = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Ref per i callback (evita stale closure nei listener)
  const onNewMessageRef = useRef(onNewMessage);
  onNewMessageRef.current = onNewMessage;
  const onConversationUpdatedRef = useRef(onConversationUpdated);
  onConversationUpdatedRef.current = onConversationUpdated;
  const onMessageErrorRef = useRef(onMessageError);
  onMessageErrorRef.current = onMessageError;

  const removeTypingUser = useCallback((representativeId: string) => {
    const timer = typingExpireRef.current.get(representativeId);
    if (timer) {
      clearTimeout(timer);
      typingExpireRef.current.delete(representativeId);
    }
    setTypingUsers(prev => prev.filter(t => t.representativeId !== representativeId));
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const token = getRepresentativeToken();
    if (!token) return;

    const socket = io(`${SOCKET_URL}/representative-comm`, {
      auth: { token },
      transports: ['websocket', 'polling']
    });
    socketRef.current = socket;

    socket.on('connect', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('new_message', (message: RepCommMessage) => {
      onNewMessageRef.current?.(message);
    });

    socket.on('conversation_updated', (data: { conversationId: string }) => {
      onConversationUpdatedRef.current?.(data);
    });

    socket.on('message_error', (data: { conversationId: string; error: string; content?: string }) => {
      onMessageErrorRef.current?.(data);
    });

    // Presence: snapshot alla connessione, poi eventi incrementali
    socket.on('online_reps', (ids: string[]) => {
      setOnlineReps(ids || []);
    });
    socket.on('rep_online', (data: { representativeId: string }) => {
      setOnlineReps(prev => (prev.includes(data.representativeId) ? prev : [...prev, data.representativeId]));
    });
    socket.on('rep_offline', (data: { representativeId: string }) => {
      setOnlineReps(prev => prev.filter(id => id !== data.representativeId));
    });

    socket.on('user_typing', (data: { representativeId: string; name: string; conversationId: string }) => {
      setTypingUsers(prev => {
        if (prev.some(t => t.representativeId === data.representativeId)) return prev;
        return [...prev, { representativeId: data.representativeId, name: data.name }];
      });
      // Auto-scadenza: se il mittente sparisce senza stop_typing, l'indicatore cade da solo
      const timers = typingExpireRef.current;
      const existing = timers.get(data.representativeId);
      if (existing) clearTimeout(existing);
      timers.set(data.representativeId, setTimeout(() => removeTypingUser(data.representativeId), TYPING_EXPIRE_MS));
    });

    socket.on('user_stopped_typing', (data: { representativeId: string }) => {
      removeTypingUser(data.representativeId);
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const timers = typingExpireRef.current;
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
  }, [enabled, removeTypingUser]);

  const joinConversation = useCallback((conversationId: string) => {
    setTypingUsers([]);
    socketRef.current?.emit('join_conversation', { conversationId });
  }, []);

  const leaveConversation = useCallback((conversationId: string) => {
    setTypingUsers([]);
    socketRef.current?.emit('leave_conversation', { conversationId });
  }, []);

  const sendMessage = useCallback((conversationId: string, content: string): boolean => {
    if (!socketRef.current || !socketRef.current.connected || !content.trim()) return false;
    socketRef.current.emit('send_message', { conversationId, content: content.trim() });
    return true;
  }, []);

  const startTyping = useCallback((conversationId: string) => {
    if (!socketRef.current?.connected) return;
    socketRef.current.emit('typing', { conversationId });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socketRef.current?.emit('stop_typing', { conversationId });
    }, 2000);
  }, []);

  const markRead = useCallback((conversationId: string) => {
    socketRef.current?.emit('mark_read', { conversationId });
  }, []);

  return {
    connected,
    onlineReps,
    typingUsers,
    joinConversation,
    leaveConversation,
    sendMessage,
    startTyping,
    markRead
  };
};

export default useRepresentativeCommSocket;
